import { ActionField, ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction";
import { SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { RowHierarchy } from "./formatMultivalue";

export type RowHierarchyOptions = {
    actionFields: ActionField[];
    otherSubproperties: SsqProperty[];
    actionPlanAction: ActionPlanAction;
    pivotSubproperty: SsqProperty;
    rowPosition: number;
    getDefaultLabel: (pos: number | undefined) => string;
};
export const getRowHierarchy = (options: RowHierarchyOptions): RowHierarchy => {
    const { actionFields, actionPlanAction, getDefaultLabel, pivotSubproperty, otherSubproperties, rowPosition } =
        options;
    const treeElements = actionPlanAction.propertiesTreeElements;
    const headerElement = treeElements.find((element) => {
        const isPivotSubproperty = element.ssq_property_id === pivotSubproperty.id;
        const isGoodPosition = element.position === rowPosition;
        return isPivotSubproperty && isGoodPosition;
    });
    const elementValue = headerElement?.value;
    const elementLabel = elementValue ?? getDefaultLabel(rowPosition);

    const title = pivotSubproperty.title !== undefined ? pivotSubproperty.title : getDefaultLabel(undefined);

    // In order to find the PARENT at this point, I need to use the OTHER SUBPROPERTIES
    // to be able to SEARCH THROUGH the actionFields.
    // In the context of a Multi value, there will be multiple parents, but all the same ones.
    const parent = otherSubproperties.reduce<string | null>((acc, subproperty) => {
        // Once found, don't search anymore;
        if (acc !== null) return acc;

        const foundParent = actionFields.flatMap((actField) => {
            const matchingParentsForSubproperty =
                actField.actionFormulas?.flatMap((formula) => {
                    const formulaSsqProperty = formula.targetSsqProperty?.id;

                    if (formulaSsqProperty !== subproperty.id) return [];

                    const parentName = formula.targetSsqProperty?.parent?.title;
                    if (parentName === undefined) return [];

                    return [parentName];
                }) ?? [];
            if (matchingParentsForSubproperty.length < 1) return [];

            const firstParent = [matchingParentsForSubproperty[0]];
            return firstParent;
        });
        if (foundParent.length < 1) return acc;

        return foundParent[0];
    }, null);

    return {
        label: elementLabel,
        title,
        parent,
    };
};
